'use client'
import { useState, useRef, useEffect } from 'react';
import CollectibleUploader from '@/components/CollectibleUploader'
import Link from 'next/link'
import { analyzeCollectibleItem, analyzeCollectibleItemAPI, detectStampIntent } from './actions';
import HttpStatus from '@/lib/HttpStatus';
import ReactMarkdown from 'react-markdown';
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';

function CollectibleCheckerWrapped() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState("");

  // NEW STATES FOR PROCESSING
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Existing camera functions from previous step (startDesktopCamera, capturePhoto, clearSelection etc...)
  // [Keep those functions completely identical here]

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // We use a free Reverse Geocoding API to turn coordinates into a Suburb name
          const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const data = await res.json();
          
          // This will now capture "Narwee" instead of just "Sydney"
          const specificLocation = `${data.locality || data.city}, ${data.principalSubdivision}`;
          setUserLocation(specificLocation);
        } catch (error) {
          console.error("Reverse geocoding failed", error);
        }
      }, (error) => {
        console.error("User denied location or error occurred", error);
      }, {
        enableHighAccuracy: true // This forces the device to use GPS if available
      });
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    if (selectedFile.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(selectedFile));
    } else if (selectedFile.type === 'application/pdf') {
      setPreview('pdf');
    }
  };

  // NEW SUBMISSION FUNCTION
  const handleAnalyze = async () => {
    console.log('Clicked');
    if (!file) return;
    setIsLoading(true);
    setAnalysisResult(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("location", userLocation);

    try {
      const response = await analyzeCollectibleItemAPI(formData);

      // const agentResponse = await detectStampIntent(formData);
      // console.log('Agent Response: ', agentResponse);

      if (response.status == HttpStatus.OK) {
        setAnalysisResult(response.data);
      } else {
        setAnalysisResult(`### Error\n${response.error || "Failed to process image."}`);
      }

      // force user tokens balance component to refresh
		  window.dispatchEvent(new Event('tokens-deducted'));
    } catch (err) {
      setAnalysisResult("### Error\nNetwork connection failure to AI processing system.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-8"> 
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Collectibles Checker"}</span>
        </div>

        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          Collectables Checker
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          You have a stamp, a coin or a banknote you want to know its details, history and value but you don't have access to the proper catalogue? <br />
          Show it to me and I'll give you its details, history and value. Enjoy! 😄
        </p>

        <AdSterraNativeBanner />

        <div className="text-center space-y-4 w-full flex flex-col justify-center items-center gap-2">
          <CollectibleUploader file={file} setFile={setFile} 
            handleAnalyze={handleAnalyze} isLoading={isLoading}
          />

          <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/png, image/jpeg, image/jpg, application/pdf" className="hidden" />

          {/* Render AI Assessment Output */}
          {analysisResult && (
            <div className="mt-4 p-5 rounded-lg bg-muted/30 border text-sm text-foreground text-left max-h-[500px] overflow-y-auto">
              {/* Using prose class handles beautiful spacing for headers, 
                lists, and bold tags automatically 
              */}
              <article className="prose prose-sm dark:prose-invert max-w-none space-y-1">
                <ReactMarkdown>{analysisResult}</ReactMarkdown>
              </article>
            </div>
          )}
        </div>
      </div>

      <AdSterraIFrameBannerAuto position='bottom'/>
    </>
  )
}

export default CollectibleCheckerWrapped